import * as React from "react";
import { useState } from "react";
import { IconButton, Tooltip, Menu, MenuItem, ListItemText } from "@mui/material";
import { SearchOutlined, NotificationsNoneOutlined, LightModeOutlined, DarkModeOutlined, LanguageOutlined, CheckOutlined } from "@mui/icons-material";
import { useTheme } from "../../providers/ThemeProvider";
import { useTranslation } from "react-i18next";
import { LANGUAGES } from "../../i18n";
import i18n from "../../i18n";
import styles from "./topBar.module.css";

export default function TopBar({ onMenuToggle }) {
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const isLight = theme === "light";

  const [langAnchor, setLangAnchor] = useState(null);
  const currentLang = i18n.language;

  const handleLangChange = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('smilefy_lang', code);
    setLangAnchor(null);
  };

  return (
    <div className={styles.bar}>

      {/* Search */}
      <div className={styles.searchWrap}>
        <span className={styles.searchIcon}><SearchOutlined sx={{ fontSize: 17 }} /></span>
        <input
          className={styles.searchInput}
          placeholder={t('topbar.searchPlaceholder')}
        />
      </div>

      {/* Right actions */}
      <div className={styles.actions}>

        {/* Language selector */}
        <Tooltip title={t('topbar.language')} arrow>
          <IconButton
            size="small"
            onClick={e => setLangAnchor(e.currentTarget)}
            sx={{
              color: isLight ? "#7c6af7" : "rgba(255,255,255,0.6)",
              bgcolor: isLight ? "rgba(124,106,247,0.1)" : "rgba(255,255,255,0.05)",
              border: `1px solid ${isLight ? "rgba(124,106,247,0.25)" : "rgba(255,255,255,0.08)"}`,
              borderRadius: 2,
              "&:hover": {
                bgcolor: isLight ? "rgba(124,106,247,0.18)" : "rgba(255,255,255,0.1)",
                color: isLight ? "#7c6af7" : "#fff",
              },
            }}
          >
            <LanguageOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={langAnchor}
          open={Boolean(langAnchor)}
          onClose={() => setLangAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              bgcolor: '#1a1a3e',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 2,
              minWidth: 200,
              maxHeight: 360,
              mt: 0.5,
            }
          }}
        >
          {LANGUAGES.map(lang => (
            <MenuItem
              key={lang.code}
              onClick={() => handleLangChange(lang.code)}
              selected={currentLang === lang.code}
              sx={{
                color: currentLang === lang.code ? '#7c6af7' : '#c8d0e0',
                fontSize: 14,
                gap: 1,
                '&.Mui-selected': { bgcolor: 'rgba(124,106,247,0.12)' },
                '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
              }}
            >
              <ListItemText primary={lang.label} />
              {currentLang === lang.code && <CheckOutlined sx={{ fontSize: 16, color: '#7c6af7' }} />}
            </MenuItem>
          ))}
        </Menu>

        <Tooltip title={isLight ? t('topbar.switchToDark') : t('topbar.switchToLight')} arrow>
          <IconButton
            size="small"
            onClick={toggleTheme}
            sx={{
              color: isLight ? "#7c6af7" : "rgba(255,255,255,0.6)",
              bgcolor: isLight ? "rgba(124,106,247,0.1)" : "rgba(255,255,255,0.05)",
              border: `1px solid ${isLight ? "rgba(124,106,247,0.25)" : "rgba(255,255,255,0.08)"}`,
              borderRadius: 2,
              "&:hover": {
                bgcolor: isLight ? "rgba(124,106,247,0.18)" : "rgba(255,255,255,0.1)",
                color: isLight ? "#7c6af7" : "#fff",
              },
            }}
          >
            {isLight
              ? <DarkModeOutlined sx={{ fontSize: 18 }} />
              : <LightModeOutlined sx={{ fontSize: 18 }} />
            }
          </IconButton>
        </Tooltip>

        <Tooltip title={t('topbar.notifications')} arrow>
          <IconButton size="small">
            <NotificationsNoneOutlined sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
}
